import { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import api from "./lib/axios";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LoginPage from "@/components/auth/LoginPage";
// Custom hooks for state management
import { useFactoryState } from "./hooks/useFactoryState";
import { useFactoryActions } from "./hooks/useFactoryActions";
import { useSalesState } from "./hooks/useSalesState";
import { useMaintenanceState } from "./hooks/useMaintenanceState";
import { 
  History,
} from "lucide-react";
import PriceTrendModal from "@/components/sales/PriceTrendModal";
// Sales modals
import { AddQuoteModal, ManualAddModal } from "@/components/sales/SalesModals";
import ParsedItemsModal from "@/components/sales/ParsedItemsModal";
// WindoorCraft components (NEW)
import { WindoorCraftParsedItemsModal } from "@/components/sales/WindoorCraftComponents";
// PDF Export Modal (NEW)
import PDFExportModal from "@/components/sales/PDFExportModal";
// Quote History Manager (NEW)
import QuoteHistoryManager from "@/components/sales/QuoteHistoryManager";
// Sales components
import QuoteTypesTable from "@/components/sales/QuoteTypesTable";
import QuoteCalculator from "@/components/sales/QuoteCalculator";
import UploadSection from "@/components/sales/UploadSection";
import TypeDetailDialog from "@/components/sales/TypeDetailDialog";
import { generatePriceIndexPDF } from "@/utils/pdfExport";
import AutocompleteInput from "@/components/common/AutocompleteInput";
import StatsCard from "@/components/common/StatsCard";
import ImageThumbnail from "@/components/common/ImageThumbnail";
import MatchConfidenceBadge from "@/components/common/MatchConfidenceBadge";
import ImagePreviewModal from "@/components/common/ImagePreviewModal";
import { formatCurrency, formatNumber } from "@/utils/formatters";
// Refactored modal components
import { 
  PriceAdjustModal, 
  AdjustmentHistoryModal 
} from "@/components/modals/PriceAdjustmentModals";
// Extracted dialog components
import SeriesManagerDialog from "@/components/modals/SeriesManagerDialog";
import DuplicateTypeModal from "@/components/modals/DuplicateTypeModal";
import MergeConfirmationDialog from "@/components/modals/MergeConfirmationDialog";
import UserManagementDialog from "@/components/modals/UserManagementDialog";
// Refactored view components
import FactoryView from "@/components/views/FactoryView";
import MaintenanceView from "@/components/views/MaintenanceView";
import SalesView from "@/components/views/SalesView";
import HistoricalView from "@/components/views/HistoricalView";
// Layout components (extracted from App.js)
import AppHeader from "@/components/layout/AppHeader";
import ProtectedSection from "@/components/common/ProtectedSection";
// Admin components
import BackupManager from "@/components/admin/BackupManager";
import PriceAlertsManager, { PriceAlertBadge } from "@/components/admin/PriceAlertsManager";
import CompanySettingsManager from "@/components/admin/CompanySettingsManager";
import ProjectsManager from "@/components/admin/ProjectsManager";
// Quotation components (NEW Section)
import QuotationView from "@/components/quotation/QuotationView";
// Refactored factory modals
import {
  FactoryManualAddModal,
  FactoryEditModal,
  FactoryDeleteModal,
  FactoryDuplicateModal
} from "@/components/factory/FactoryModals";
import FactoryParsedItemsModal from "@/components/factory/FactoryParsedItemsModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Main App Component
function App({ currentUser, onLogout, isAdmin }) {
  // ==================== Sales State from Hook ====================
  const salesState = useSalesState();
  const {
    quoteTypes, setQuoteTypes,
    dashboardStats, setDashboardStats,
    materials, setMaterials,
    aluminumSeries, setAluminumSeries,
    availableColors,
    salesFactories, setSalesFactories,
    isLoading, setIsLoading,
    salesProjectFilter, setSalesProjectFilter,
    salesGlassFilter, setSalesGlassFilter,
    salesExtrasFilter, setSalesExtrasFilter,
    availableSalesGlassSpecs, setAvailableSalesGlassSpecs,
    availableSalesExtrasSpecs, setAvailableSalesExtrasSpecs,
    selectedType, setSelectedType,
    detailType, setDetailType,
    isDetailOpen, setIsDetailOpen,
    isAddSeriesOpen, setIsAddSeriesOpen,
    isAddQuoteOpen, setIsAddQuoteOpen,
    newSeriesName, setNewSeriesName,
    addQuoteType, setAddQuoteType,
    newQuote, setNewQuote,
    isParsedItemsOpen, setIsParsedItemsOpen,
    parsedItems, setParsedItems,
    parsedFilename, setParsedFilename,
    newImportSeries, setNewImportSeries,
    salesProjectName, setSalesProjectName,
    dimensionUnit, setDimensionUnit,
    importAllImages, setImportAllImages,
    isManualAddOpen, setIsManualAddOpen,
    manualAddData, setManualAddData,
    isManualAddSubmitting, setIsManualAddSubmitting,
    trendModalType, setTrendModalType,
    duplicateModalType, setDuplicateModalType,
    duplicateNewName, setDuplicateNewName,
    isDuplicating, setIsDuplicating,
    isPriceAdjustOpen, setIsPriceAdjustOpen,
    priceAdjustData, setPriceAdjustData,
    isAdjusting, setIsAdjusting,
    selectedTypeIds, setSelectedTypeIds,
    isHistoryOpen, setIsHistoryOpen,
    adjustmentHistory, setAdjustmentHistory,
    isLoadingHistory, setIsLoadingHistory,
    isUndoing, setIsUndoing,
    windoorCraftLoading, setWindoorCraftLoading,
    windoorCraftParsedData, setWindoorCraftParsedData,
    isWindoorCraftModalOpen, setIsWindoorCraftModalOpen,
    isWindoorCraftSaving, setIsWindoorCraftSaving,
    installationData, setInstallationData,
    pdrData, setPdrData,
    totalSupplyValue, setTotalSupplyValue,
    isPDFExportModalOpen, setIsPDFExportModalOpen,
    isQuoteHistoryOpen, setIsQuoteHistoryOpen,
    fetchAllSalesData,
    fetchMaterials,
    fetchQuoteTypes,
    fetchDashboardStats,
    fetchSalesFilterOptions
  } = salesState;

  // Navigation using React Router
  const location = useLocation();
  const navigate = useNavigate();
  
  // Derive activeView from URL path
  const activeView = useMemo(() => {
    const path = location.pathname;
    if (path === '/quotation' || path.startsWith('/quotation/')) return 'quotation';
    if (path === '/factory' || path.startsWith('/factory/')) return 'factory';
    if (path === '/maintenance' || path.startsWith('/maintenance/')) return 'maintenance';
    if (path === '/historical' || path.startsWith('/historical/')) return 'historical';
    return 'main';
  }, [location.pathname]);
  
  // Navigation function that updates URL
  const setActiveView = useCallback((view) => {
    const routes = {
      'main': '/',
      'quotation': '/quotation',
      'factory': '/factory',
      'maintenance': '/maintenance',
      'historical': '/historical'
    };
    navigate(routes[view] || '/');
  }, [navigate]);
  
  const [isBackupManagerOpen, setIsBackupManagerOpen] = useState(false);
  const [isPriceAlertsOpen, setIsPriceAlertsOpen] = useState(false);
  const [isCompanySettingsOpen, setIsCompanySettingsOpen] = useState(false);
  const [isProjectsManagerOpen, setIsProjectsManagerOpen] = useState(false);
  const [isUsersOpen, setIsUsersOpen] = useState(false);

  // Use Factory state hook (all factory state managed in useFactoryState)
  const factoryState = useFactoryState();
  const {
    factoryPassword, setFactoryPassword,
    factoryAuthenticated, setFactoryAuthenticated,
    factoryTypes, setFactoryTypes,
    factoryStats, setFactoryStats,
    isFactoryParsedItemsOpen, setIsFactoryParsedItemsOpen,
    factoryParsedItems, setFactoryParsedItems,
    factoryParsedFilename, setFactoryParsedFilename,
    factoryProjectName, setFactoryProjectName,
    factoryInvoiceNo, setFactoryInvoiceNo,
    isFactoryUploading, setIsFactoryUploading,
    factoryAllImages, setFactoryAllImages,
    factoryImageSelectorOpen, setFactoryImageSelectorOpen,
    factoryBulkMaterial, setFactoryBulkMaterial,
    factoryBulkColor, setFactoryBulkColor,
    factoryBulkFactory, setFactoryBulkFactory,
    availableFactories, setAvailableFactories,
    imageSuggestionsOpen, setImageSuggestionsOpen,
    imageSuggestions, setImageSuggestions,
    factoryFilterFactory, setFactoryFilterFactory,
    factorySearchQuery, setFactorySearchQuery,
    factoryFilterMaterial, setFactoryFilterMaterial,
    factoryFilterColor, setFactoryFilterColor,
    factoryFilterGlass, setFactoryFilterGlass,
    factoryFilterExtras, setFactoryFilterExtras,
    factoryFilterProject, setFactoryFilterProject,
    factoryDimensionUnit, setFactoryDimensionUnit,
    factoryStatsByFactory, setFactoryStatsByFactory,
    allProjects, setAllProjects,
    factoryAdjustPercent, setFactoryAdjustPercent,
    factoryAdjustApplied, setFactoryAdjustApplied,
    allTypeNames, setAllTypeNames,
    isFactoryManualAddOpen, setIsFactoryManualAddOpen,
    factoryManualItem, setFactoryManualItem,
    factoryManualDragActive, setFactoryManualDragActive,
    factoryEditType, setFactoryEditType,
    isFactoryEditOpen, setIsFactoryEditOpen,
    factoryDeleteType, setFactoryDeleteType,
    isFactoryDeleteOpen, setIsFactoryDeleteOpen,
    factoryDuplicateType, setFactoryDuplicateType,
    factoryDuplicateName, setFactoryDuplicateName,
    isFactoryDuplicateOpen, setIsFactoryDuplicateOpen,
    handleFactoryAuth: hookHandleFactoryAuth,
    fetchFactoryData: hookFetchFactoryData,
    loadImageSuggestions,
    loadAllProjects,
    loadAllTypeNames,
    clearFactoryFilters,
    resetFactoryManualItem,
    openFactoryEditModal,
    openFactoryDuplicateModal,
    openFactoryDeleteModal,
    // Lazy loading
    totalItems,
    hasMoreData,
    isLoadingMore,
    loadMoreFactoryTypes,
    reloadFactoryTypes
  } = factoryState;

  // Factory actions from hook (extracted from App.js)
  const factoryActions = useFactoryActions(factoryState, { fetchQuoteTypes, fetchAllSalesData });
  const {
    mergeConfirmation,
    autoMatchProjectItems,
    handleProjectNameChange,
    applyFactoryBulkFactory,
    handleFactoryManualImage,
    handleFactoryManualDrop,
    handleFactoryManualPaste,
    handleSaveFactoryManualType,
    handleUpdateFactoryType,
    handleConfirmMerge,
    handleCancelMerge,
    handleDeleteFactoryType,
    handleDuplicateFactoryType,
    handleFactoryFileUpload,
    updateFactoryParsedItem,
    applyFactoryBulkMaterial,
    applyFactoryBulkColor,
    applyFactoryPercentAdjust,
    saveFactoryParsedItems
  } = factoryActions;

  // Maintenance state from hook
  const maintenanceState = useMaintenanceState(activeView, factoryPassword);
  const {
    maintenanceTypes,
    maintenanceSearchQuery,
    setMaintenanceSearchQuery,
    fetchMaintenanceTypes,
    selectedMergeSource,
    setSelectedMergeSource,
    selectedMergeTarget,
    setSelectedMergeTarget,
    handleMergeTypes: hookHandleMergeTypes,
    similarTypes,
    setSimilarTypes,
    isLoadingSimilar,
    findSimilarTypes,
    maintenanceProjects,
    selectedProject,
    setSelectedProject,
    projectAdjustPercent,
    setProjectAdjustPercent,
    isAdjustingProject,
    handleAdjustProjectCosts: hookHandleAdjustProjectCosts,
    handleRenameType: hookHandleRenameType
  } = maintenanceState;

  // ==================== Data Fetching Functions ====================
  // Note: fetchMaterials, fetchQuoteTypes, etc are now in useSalesState hook

  // Wrapper for fetchAllData from hook
  const fetchAllData = fetchAllSalesData;

  // Wrapper functions for maintenance that need external callbacks
  const handleMergeTypes = () => hookHandleMergeTypes({ fetchAllData });
  const handleAdjustProjectCosts = () => hookHandleAdjustProjectCosts({ fetchFactoryData });
  const handleRenameType = (typeId, newName) => hookHandleRenameType(typeId, newName, { fetchAllData });

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Global paste handler for manual add modal
  useEffect(() => {
    const handleGlobalPaste = (e) => {
      if (!isManualAddOpen) return;
      
      const items = e.clipboardData?.items;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          if (items[i].type.indexOf('image') !== -1) {
            e.preventDefault();
            const blob = items[i].getAsFile();
            const reader = new FileReader();
            reader.onloadend = () => {
              setManualAddData(prev => ({ ...prev, image_data: reader.result }));
              toast.success('Imagen pegada correctamente');
            };
            reader.readAsDataURL(blob);
            break;
          }
        }
      }
    };
    
    document.addEventListener('paste', handleGlobalPaste);
    return () => document.removeEventListener('paste', handleGlobalPaste);
  }, [isManualAddOpen, setManualAddData]);

  // Factory section functions - use hook functions
  const handleFactoryAuth = hookHandleFactoryAuth;
  const fetchFactoryData = hookFetchFactoryData;

  // deleteFactoryType is used in FactoryView separately
  const deleteFactoryType = async (typeId) => {
    if (!window.confirm('¿Eliminar este tipo de costo de fábrica?')) return;
    
    try {
      await api.delete(`${API}/factory/types/${typeId}`, {
        headers: { 'X-Factory-Password': factoryPassword }
      });
      toast.success('Tipo eliminado');
      fetchFactoryData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Price adjustment functions

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este tipo?')) return;
    
    try {
      await api.delete(`${API}/quote-types/${id}`);
      toast.success('Tipo eliminado');
      fetchAllData();
      if (selectedType?.id === id) {
        setSelectedType(null);
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleDeleteMultiple = async (ids) => {
    if (!window.confirm(`¿Estás seguro de eliminar ${ids.length} tipos?`)) return;
    
    try {
      const response = await api.post(`${API}/quote-types/delete-multiple`, { ids });
      toast.success(response.data.message);
      fetchAllData();
      // Clear selection if selected type was deleted
      if (selectedType && ids.includes(selectedType.id)) {
        setSelectedType(null);
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleViewDetail = async (type) => {
    try {
      const response = await api.get(`${API}/quote-types/${type.id}`);
      setDetailType(response.data);
      setIsDetailOpen(true);
    } catch (error) {
      toast.error('Error al cargar detalle');
    }
  };

  // Add new series
  const handleAddSeries = async () => {
    if (!newSeriesName.trim()) {
      toast.error('Ingresa un nombre para la serie');
      return;
    }
    
    try {
      await api.post(`${API}/series`, { series_name: newSeriesName.trim() });
      toast.success(`Serie "${newSeriesName}" agregada`);
      setNewSeriesName("");
      setIsAddSeriesOpen(false);
      fetchMaterials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar serie');
    }
  };

  // Delete custom series
  const handleDeleteSeries = async (seriesName) => {
    if (!window.confirm(`¿Eliminar la serie "${seriesName}"?`)) return;
    
    try {
      await api.delete(`${API}/series/${encodeURIComponent(seriesName)}`);
      toast.success(`Serie "${seriesName}" eliminada`);
      fetchMaterials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar serie');
    }
  };

  // Open add quote modal
  const openAddQuoteModal = (type) => {
    setAddQuoteType(type);
    setNewQuote({ width: "", height: "", cost: "", notes: "" });
    setIsAddQuoteOpen(true);
  };

  // Add quote entry to existing type
  const handleAddQuoteEntry = async () => {
    const { width, height, cost, notes } = newQuote;
    
    if (!width || !height || !cost) {
      toast.error('Completa todos los campos requeridos');
      return;
    }
    
    const w = parseFloat(width);
    const h = parseFloat(height);
    const c = parseFloat(cost);
    
    if (isNaN(w) || isNaN(h) || isNaN(c) || w <= 0 || h <= 0 || c <= 0) {
      toast.error('Los valores deben ser números mayores a 0');
      return;
    }
    
    try {
      const response = await api.post(`${API}/quote-entries`, {
        quote_type_id: addQuoteType.id,
        width: w,
        height: h,
        cost: c,
        notes: notes || null
      });
      
      toast.success(
        `Cotización agregada. Nuevo promedio: ${formatCurrency(response.data.new_average_cost_per_m2)}/m²`,
        { duration: 5000 }
      );
      setIsAddQuoteOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar cotización');
    }
  };

  // Handle manual image upload for manual add
  const handleManualImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setManualAddData(prev => ({ ...prev, image_data: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  // Submit manual type creation
  const handleManualAddSubmit = async () => {
    const { name, material, width_m, height_m, cost } = manualAddData;
    
    // Validation
    if (!name?.trim()) {
      toast.error('Ingresa un nombre/código para el tipo');
      return;
    }
    if (!material) {
      toast.error('Selecciona un material');
      return;
    }
    if (!width_m || !height_m || !cost) {
      toast.error('Completa ancho, alto y costo');
      return;
    }
    
    const w = parseFloat(width_m);
    const h = parseFloat(height_m);
    const c = parseFloat(cost);
    
    if (isNaN(w) || isNaN(h) || isNaN(c) || w <= 0 || h <= 0 || c <= 0) {
      toast.error('Ancho, alto y costo deben ser números mayores a 0');
      return;
    }
    
    setIsManualAddSubmitting(true);
    try {
      const response = await api.post(`${API}/quote-types/manual`, {
        name: name.trim(),
        material,
        series: manualAddData.series || null,
        color: manualAddData.color || null,
        glass_spec: manualAddData.glass_spec || null,
        extra_specs: manualAddData.extra_specs || null,
        width_m: w,
        height_m: h,
        cost: c,
        image_data: manualAddData.image_data || null
      });
      
      toast.success(response.data.message, { duration: 5000 });
      setIsManualAddOpen(false);
      setManualAddData({
        name: "",
        material: "",
        series: "",
        color: "",
        glass_spec: "",
        extra_specs: "",
        width_m: "",
        height_m: "",
        cost: "",
        image_data: null
      });
      fetchAllData();
      // Also refresh filter options since we may have added new glass_spec or extra_specs
      fetchSalesFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear tipo manualmente');
    } finally {
      setIsManualAddSubmitting(false);
    }
  };

  // Handle parsed quote file
  const handleParseQuoteFile = async (data) => {
    // Load type names for autocomplete
    await loadAllTypeNames();
    
    setParsedFilename(data.filename);
    setParsedItems(data.items.map(item => ({
      ...item,
      // Use matched type from image hash comparison if available
      // If series is detected (e.g. "SERIE 60"), default to Aluminio
      material: item.material || (item.series ? "Aluminio" : null),
      // Pre-fill name from matched type or leave empty for user input
      customName: item.matched_type_name || "",
      // Use matched type ID if image match was found
      useExistingType: item.matched_type_id || null,
      // Initialize color as null
      color: item.color || null,
      // Store match confidence for visual indicator
      matchConfidence: item.match_confidence || null
    })));
    
    // Store all images for manual selection
    const allImgs = data.items
      .filter(item => item.image_data)
      .map(item => item.image_data);
    setImportAllImages([...new Set(allImgs)]); // Unique images
    
    setIsParsedItemsOpen(true);
  };

  // Handle WindoorCraft parsed file (NEW)
  const handleWindoorCraftParsed = (data) => {
    setWindoorCraftParsedData(data);
    setIsWindoorCraftModalOpen(true);
  };

  // Save WindoorCraft items (NEW)
  const handleSaveWindoorCraftItems = async (saveData) => {
    setIsWindoorCraftSaving(true);
    try {
      const response = await api.post(`${API}/sales/save-windoorcraft-items`, saveData);
      
      if (response.data.saved_items > 0) {
        toast.success(`Se guardaron ${response.data.saved_items} elementos (${response.data.new_types} nuevos, ${response.data.updated_types} actualizados)`);
        await fetchQuoteTypes();
        await fetchDashboardStats();
        setIsWindoorCraftModalOpen(false);
        setWindoorCraftParsedData(null);
      }
      
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(error => toast.warning(error));
      }
    } catch (error) {
      // console.error("Error saving WindoorCraft items:", error);
      toast.error(error.response?.data?.detail || "Error al guardar elementos");
    } finally {
      setIsWindoorCraftSaving(false);
    }
  };

  // Update image for a parsed item
  const updateParsedItemImage = (itemId, imageData) => {
    setParsedItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, image_data: imageData, image_hash: null } : item
    ));
  };

  // Update material for a parsed item
  const updateParsedItemMaterial = (itemId, material) => {
    setParsedItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, material } : item
    ));
  };

  // Update color for a parsed item
  const updateParsedItemColor = (itemId, color) => {
    setParsedItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, color } : item
    ));
  };

  // Update custom name for a parsed item
  const updateParsedItemName = (itemId, customName) => {
    setParsedItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, customName, useExistingType: null } : item
    ));
  };

  // Select existing type for a parsed item
  const selectExistingTypeForItem = (itemId, existingType) => {
    setParsedItems(prev => prev.map(item => 
      item.id === itemId ? { 
        ...item, 
        useExistingType: existingType?.id || null,
        customName: existingType?.name || "",
        material: existingType?.material || item.material,
        series: existingType?.series || item.series,
        color: existingType?.color || item.color,
        glass_spec: existingType?.glass_spec || item.glass_spec
      } : item
    ));
  };

  // Set material for all items
  const setAllItemsMaterial = (material) => {
    setParsedItems(prev => prev.map(item => ({ ...item, material })));
  };

  // Set color for all items
  const setAllItemsColor = (color) => {
    setParsedItems(prev => prev.map(item => ({ ...item, color })));
  };

  // Update series for a parsed item
  const updateParsedItemSeries = (itemId, series) => {
    setParsedItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, series } : item
    ));
  };

  // Update dimensions (width, height, cost) for a parsed item
  const updateParsedItemDimensions = (itemId, updates) => {
    setParsedItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, ...updates } : item
    ));
  };

  // Set series for all items
  const setAllItemsSeries = (series) => {
    setParsedItems(prev => prev.map(item => ({ ...item, series })));
  };

  // Glass spec (per item / all items)
  const updateParsedItemGlass = (itemId, glass_spec) => {
    setParsedItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, glass_spec } : item
    ));
  };

  const setAllItemsGlass = (glass_spec) => {
    setParsedItems(prev => prev.map(item => ({ ...item, glass_spec })));
  };

  // Create new catalog values (material / color / series) from the import modal
  const createCatalogValue = async (kind, name) => {
    const endpoints = {
      material: [`${API}/materials`, { material_name: name }, 'Material'],
      color: [`${API}/colors`, { color_name: name }, 'Color'],
      series: [`${API}/series`, { series_name: name }, 'Serie'],
    };
    const [url, body, label] = endpoints[kind];
    try {
      const res = await api.post(url, body);
      toast.success(`${label} "${name}" agregad${label === 'Serie' ? 'a' : 'o'}`);
      await fetchMaterials();
      return res.data[kind] || name;
    } catch (error) {
      toast.error(error.response?.data?.detail || `Error al agregar ${label.toLowerCase()}`);
      return false;
    }
  };

  // Image preview state
  const [previewImage, setPreviewImage] = useState({ isOpen: false, url: null, title: null });

  const openImagePreview = (url, title) => {
    setPreviewImage({ isOpen: true, url, title });
  };

  const closeImagePreview = () => {
    setPreviewImage({ isOpen: false, url: null, title: null });
  };

  // Save parsed items
  const saveParsedItems = async () => {
    const itemsWithoutMaterial = parsedItems.filter(item => !item.material);
    if (itemsWithoutMaterial.length > 0) {
      toast.error(`${itemsWithoutMaterial.length} elementos sin material asignado`);
      return;
    }
    
    // Check that all items have a custom name or use existing type
    const itemsWithoutName = parsedItems.filter(item => !item.customName?.trim() && !item.useExistingType);
    if (itemsWithoutName.length > 0) {
      toast.error(`${itemsWithoutName.length} elementos sin nombre de tipo asignado`);
      return;
    }
    
    try {
      // Prepare items with custom names or existing type references
      const itemsToSave = parsedItems.map(item => ({
        ...item,
        // Use customName (required now)
        final_name: item.customName?.trim(),
        existing_type_id: item.useExistingType || null,
        project_name: salesProjectName || null  // Include project name
      }));
      
      const response = await api.post(`${API}/save-parsed-items`, {
        items: itemsToSave,
        project_name: salesProjectName || null  // Also send at request level
      });
      
      toast.success(
        `Guardado: ${response.data.new_types} nuevos, ${response.data.updated_types} actualizados`,
        { duration: 5000 }
      );
      
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} errores`);
      }
      
      setIsParsedItemsOpen(false);
      setParsedItems([]);
      setSalesProjectName("");  // Clear project name
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  // Export data to CSV or PDF
  const handleExport = async (dataToExport, format = 'csv') => {
    if (format === 'pdf') {
      // Export to PDF with images
      toast.info('Generando PDF con imágenes...', { duration: 2000 });
      try {
        // Calculate stats for PDF
        const stats = {
          totalTypes: dataToExport.length,
          averagePrice: dataToExport.length > 0 
            ? dataToExport.reduce((sum, t) => sum + (t.average_cost_per_m2 || 0), 0) / dataToExport.length 
            : 0,
          totalArea: dataToExport.reduce((sum, t) => sum + (t.total_area || 0), 0),
          totalSamples: dataToExport.reduce((sum, t) => sum + (t.entries_count || 0), 0)
        };
        
        const fileName = await generatePriceIndexPDF(dataToExport, stats);
        toast.success(`PDF generado: ${fileName}`, { duration: 4000 });
      } catch (error) {
        // console.error('Error generating PDF:', error);
        toast.error('Error al generar PDF');
      }
      return;
    }
    
    // Export to CSV
    const headers = ['Tipo', 'Material', 'Color', 'Serie', 'Muestras', 'Promedio/m²', 'Mínimo/m²', 'Máximo/m²', 'Área Total'];
    const rows = dataToExport.map(type => [
      type.name,
      type.material,
      type.color || '',
      type.series || '',
      type.entries_count,
      type.average_cost_per_m2,
      type.min_cost_per_m2,
      type.max_cost_per_m2,
      type.total_area
    ]);
    
    // Build CSV string
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Create blob and download
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `AluMetric_Indice_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Exportados ${dataToExport.length} elementos`);
  };

  // Handle duplicate type
  const handleDuplicate = async () => {
    if (!duplicateModalType || !duplicateNewName.trim()) {
      toast.error('Ingresa un nombre para el nuevo tipo');
      return;
    }
    
    setIsDuplicating(true);
    try {
      const response = await api.post(`${API}/quote-types/${duplicateModalType.id}/duplicate`, {
        new_name: duplicateNewName.trim()
      });
      toast.success(response.data.message, { duration: 4000 });
      setDuplicateModalType(null);
      setDuplicateNewName("");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al duplicar tipo');
    } finally {
      setIsDuplicating(false);
    }
  };

  // Handle price adjustment
  const handlePriceAdjust = async () => {
    const percentage = parseFloat(priceAdjustData.percentage);
    if (isNaN(percentage) || percentage === 0) {
      toast.error('Ingresa un porcentaje válido (distinto de 0)');
      return;
    }
    
    setIsAdjusting(true);
    try {
      const requestData = {
        percentage
      };
      
      // Add selected IDs if applying to selection
      if (priceAdjustData.applyTo === "selected") {
        if (selectedTypeIds.length === 0) {
          toast.error('No hay tipos seleccionados');
          return;
        }
        requestData.type_ids = selectedTypeIds;
      }
      // Add filters if not applying to all
      else if (priceAdjustData.applyTo === "filtered") {
        requestData.filters = {};
        if (priceAdjustData.filterMaterial) {
          requestData.filters.material = priceAdjustData.filterMaterial;
        }
        if (priceAdjustData.filterColor) {
          requestData.filters.color = priceAdjustData.filterColor;
        }
        if (priceAdjustData.filterName) {
          requestData.filters.name_contains = priceAdjustData.filterName;
        }
      }
      
      const response = await api.post(`${API}/quote-types/adjust-prices`, requestData);
      toast.success(response.data.message, { duration: 5000 });
      setIsPriceAdjustOpen(false);
      setPriceAdjustData({
        percentage: "",
        applyTo: "all",
        filterMaterial: "",
        filterColor: "",
        filterName: ""
      });
      setSelectedTypeIds([]); // Clear selection
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al ajustar precios');
    } finally {
      setIsAdjusting(false);
    }
  };

  // Fetch adjustment history
  const fetchAdjustmentHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const response = await api.get(`${API}/price-adjustments?limit=50`);
      setAdjustmentHistory(response.data.adjustments || []);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Undo an adjustment
  const handleUndoAdjustment = async (adjustmentId) => {
    if (!window.confirm('¿Revertir este ajuste? Los precios volverán a sus valores anteriores.')) return;
    
    setIsUndoing(adjustmentId);
    try {
      const response = await api.post(`${API}/price-adjustments/${adjustmentId}/undo`);
      toast.success(response.data.message, { duration: 5000 });
      fetchAdjustmentHistory();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al revertir ajuste');
    } finally {
      setIsUndoing(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="app-container">
      <Toaster position="top-right" richColors />
      
      {/* Header - Extracted Component */}
      <AppHeader
        activeView={activeView}
        setActiveView={setActiveView}
        currentUser={currentUser}
        isAdmin={isAdmin}
        onLogout={onLogout}
        onOpenBackupManager={() => setIsBackupManagerOpen(true)}
        onOpenProjectsManager={() => setIsProjectsManagerOpen(true)}
        onOpenCompanySettings={() => setIsCompanySettingsOpen(true)}
        onOpenUserManagement={() => setIsUsersOpen(true)}
        onOpenSeriesManager={() => setIsAddSeriesOpen(true)}
        onOpenPriceAlerts={() => setIsPriceAlertsOpen(true)}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
          </div>
        ) : activeView === "main" ? (
          /* ==================== SALES VIEW (Refactored Component) ==================== */
          <SalesView
            // Dashboard stats
            dashboardStats={dashboardStats}
            
            // Upload section props
            onParseQuoteFile={handleParseQuoteFile}
            onManualAdd={() => { loadAllTypeNames(); setIsManualAddOpen(true); }}
            
            // Calculator props
            quoteTypes={quoteTypes}
            selectedType={selectedType}
            onTypeChange={setSelectedType}
            materials={materials}
            aluminumSeries={aluminumSeries}
            
            // Table props
            onDelete={handleDelete}
            onDeleteMultiple={handleDeleteMultiple}
            onViewDetail={handleViewDetail}
            onSelectForCalculator={setSelectedType}
            onAddQuote={openAddQuoteModal}
            onViewTrend={(type) => setTrendModalType(type)}
            onDuplicate={(type) => {
              setDuplicateModalType(type);
              setDuplicateNewName(type.name + " (copia)");
            }}
            onPriceAdjust={() => setIsPriceAdjustOpen(true)}
            onOpenHistory={() => {
              setIsHistoryOpen(true);
              fetchAdjustmentHistory();
            }}
            selectedTypeIds={selectedTypeIds}
            onSelectionChange={setSelectedTypeIds}
            onExport={handleExport}
            onImagePreview={openImagePreview}
            availableFactories={salesFactories}
            
            // Project filter
            projectFilter={salesProjectFilter}
            onProjectFilterChange={setSalesProjectFilter}
            allProjects={allProjects}
            
            // Glass and Extras filters (NEW)
            glassFilter={salesGlassFilter}
            onGlassFilterChange={setSalesGlassFilter}
            availableGlassSpecs={availableSalesGlassSpecs}
            extrasFilter={salesExtrasFilter}
            onExtrasFilterChange={setSalesExtrasFilter}
            availableExtrasSpecs={availableSalesExtrasSpecs}
            
            // WindoorCraft props (NEW)
            onWindoorCraftParsed={handleWindoorCraftParsed}
            windoorCraftLoading={windoorCraftLoading}
            setWindoorCraftLoading={setWindoorCraftLoading}
            
            // Installation & PDR props (NEW)
            installationData={installationData}
            setInstallationData={setInstallationData}
            pdrData={pdrData}
            setPdrData={setPdrData}
            totalSupply={totalSupplyValue}
            
            // PDF Export (NEW)
            onOpenPDFExport={() => setIsPDFExportModalOpen(true)}
            
            // Quote History (NEW)
            onOpenQuoteHistory={() => setIsQuoteHistoryOpen(true)}
            
            // Refresh callback for data reload after edits
            onRefresh={fetchQuoteTypes}
          />
        ) : activeView === "quotation" ? (
          /* ==================== QUOTATION VIEW (New Section) ==================== */
          <Routes>
            <Route path="/quotation/:code" element={<QuotationView />} />
            <Route path="/quotation" element={<QuotationView />} />
            <Route path="*" element={<QuotationView />} />
          </Routes>
        ) : activeView === "factory" ? (
          /* ==================== FACTORY VIEW (Refactored Component) ==================== */
          <FactoryView
            // Auth state
            factoryAuthenticated={factoryAuthenticated}
            factoryPassword={factoryPassword}
            setFactoryPassword={setFactoryPassword}
            handleFactoryAuth={handleFactoryAuth}
            
            // Factory data
            factoryTypes={factoryTypes}
            factoryStats={factoryStats}
            factoryStatsByFactory={factoryStatsByFactory}
            availableFactories={availableFactories}
            materials={materials}
            
            // Upload state
            isFactoryUploading={isFactoryUploading}
            onFactoryFileUpload={handleFactoryFileUpload}
            onManualAddOpen={() => { loadAllTypeNames(); setIsFactoryManualAddOpen(true); }}
            
            // Filter state
            factoryFilterFactory={factoryFilterFactory}
            setFactoryFilterFactory={setFactoryFilterFactory}
            factoryFilterMaterial={factoryFilterMaterial}
            setFactoryFilterMaterial={setFactoryFilterMaterial}
            factoryFilterColor={factoryFilterColor}
            setFactoryFilterColor={setFactoryFilterColor}
            factoryFilterGlass={factoryFilterGlass}
            setFactoryFilterGlass={setFactoryFilterGlass}
            factoryFilterExtras={factoryFilterExtras}
            setFactoryFilterExtras={setFactoryFilterExtras}
            factoryFilterProject={factoryFilterProject}
            setFactoryFilterProject={setFactoryFilterProject}
            factorySearchQuery={factorySearchQuery}
            setFactorySearchQuery={setFactorySearchQuery}
            
            // Actions
            openFactoryEditModal={openFactoryEditModal}
            openFactoryDuplicateModal={openFactoryDuplicateModal}
            openFactoryDeleteModal={openFactoryDeleteModal}
            openImagePreview={openImagePreview}
            refreshFactoryData={hookFetchFactoryData}
            
            // Lazy loading
            totalItems={totalItems}
            hasMoreData={hasMoreData}
            isLoadingMore={isLoadingMore}
            loadMoreFactoryTypes={loadMoreFactoryTypes}
            reloadFactoryTypes={reloadFactoryTypes}
          />
        ) : activeView === "maintenance" ? (
          /* ==================== MAINTENANCE VIEW (Refactored Component) ==================== */
          <MaintenanceView
            // Maintenance types data
            maintenanceTypes={maintenanceTypes}
            maintenanceSearchQuery={maintenanceSearchQuery}
            setMaintenanceSearchQuery={setMaintenanceSearchQuery}
            
            // Merge state
            selectedMergeSource={selectedMergeSource}
            setSelectedMergeSource={setSelectedMergeSource}
            selectedMergeTarget={selectedMergeTarget}
            setSelectedMergeTarget={setSelectedMergeTarget}
            handleMergeTypes={handleMergeTypes}
            
            // Similar types
            similarTypes={similarTypes}
            setSimilarTypes={setSimilarTypes}
            isLoadingSimilar={isLoadingSimilar}
            findSimilarTypes={findSimilarTypes}
            
            // Project management
            maintenanceProjects={maintenanceProjects}
            selectedProject={selectedProject}
            setSelectedProject={setSelectedProject}
            projectAdjustPercent={projectAdjustPercent}
            setProjectAdjustPercent={setProjectAdjustPercent}
            handleAdjustProjectCosts={handleAdjustProjectCosts}
            isAdjustingProject={isAdjustingProject}
            
            // Actions
            openImagePreview={openImagePreview}
            toast={toast}
          />
        ) : activeView === "historical" ? (
          /* ==================== HISTORICAL VIEW (Protected by Factory Password) ==================== */
          factoryPassword ? (
            <HistoricalView />
          ) : (
            <ProtectedSection
              title="Sección Histórico Protegida"
              description="Ingrese la contraseña de fábrica para acceder al histórico de proyectos."
              icon={History}
              iconClassName="text-amber-500"
              onPasswordSubmit={setFactoryPassword}
            />
          )
        ) : null}
      </main>

      {/* Detail Dialog */}
      <TypeDetailDialog
        type={detailType}
        isOpen={isDetailOpen}
        onClose={() => { setIsDetailOpen(false); fetchAllData(); }}
        onRefresh={fetchAllData}
      />

      {/* Add Series Dialog (Extracted Component) */}
      <SeriesManagerDialog
        isOpen={isAddSeriesOpen}
        onClose={setIsAddSeriesOpen}
        aluminumSeries={aluminumSeries}
        newSeriesName={newSeriesName}
        setNewSeriesName={setNewSeriesName}
        onAddSeries={handleAddSeries}
        onDeleteSeries={handleDeleteSeries}
      />

      {/* Add Quote Entry Dialog */}
      {/* Add Quote Modal (Refactored Component) */}
      <AddQuoteModal
        isOpen={isAddQuoteOpen}
        onClose={setIsAddQuoteOpen}
        addQuoteType={addQuoteType}
        newQuote={newQuote}
        setNewQuote={setNewQuote}
        onSubmit={handleAddQuoteEntry}
      />

      {/* Parsed Items Review Dialog (Refactored Component) */}
      <ParsedItemsModal
        isOpen={isParsedItemsOpen}
        onClose={setIsParsedItemsOpen}
        parsedItems={parsedItems}
        parsedFilename={parsedFilename}
        importAllImages={importAllImages}
        allTypeNames={allTypeNames}
        allProjects={allProjects}
        aluminumSeries={aluminumSeries}
        quoteTypes={quoteTypes}
        materials={materials}
        dimensionUnit={dimensionUnit}
        setDimensionUnit={setDimensionUnit}
        salesProjectName={salesProjectName}
        setSalesProjectName={setSalesProjectName}
        setAllItemsMaterial={setAllItemsMaterial}
        setAllItemsColor={setAllItemsColor}
        setAllItemsSeries={setAllItemsSeries}
        updateParsedItemName={updateParsedItemName}
        updateParsedItemImage={updateParsedItemImage}
        updateParsedItemMaterial={updateParsedItemMaterial}
        updateParsedItemColor={updateParsedItemColor}
        updateParsedItemSeries={updateParsedItemSeries}
        updateParsedItemGlass={updateParsedItemGlass}
        setAllItemsGlass={setAllItemsGlass}
        availableColors={availableColors}
        availableGlassSpecs={availableSalesGlassSpecs}
        createCatalogValue={createCatalogValue}
        updateParsedItemDimensions={updateParsedItemDimensions}
        selectExistingTypeForItem={selectExistingTypeForItem}
        newImportSeries={newImportSeries}
        setNewImportSeries={setNewImportSeries}
        fetchMaterials={fetchMaterials}
        saveParsedItems={saveParsedItems}
        API={API}
        toast={toast}
      />

      {/* WindoorCraft Parsed Items Modal (NEW) */}
      <WindoorCraftParsedItemsModal
        isOpen={isWindoorCraftModalOpen}
        onClose={() => {
          setIsWindoorCraftModalOpen(false);
          setWindoorCraftParsedData(null);
        }}
        parsedData={windoorCraftParsedData}
        onSave={handleSaveWindoorCraftItems}
        isSaving={isWindoorCraftSaving}
        aluminumSeries={aluminumSeries}
      />

      {/* PDF Export Modal (NEW) */}
      <PDFExportModal
        isOpen={isPDFExportModalOpen}
        onClose={() => setIsPDFExportModalOpen(false)}
        quoteTypes={quoteTypes}
        installationData={installationData}
        pdrData={pdrData}
      />

      {/* Image Preview Modal */}
      <ImagePreviewModal 
        imageUrl={previewImage.url}
        isOpen={previewImage.isOpen}
        onClose={closeImagePreview}
        title={previewImage.title}
      />

      {/* Factory Parsed Items Review Dialog (Refactored Component) */}
      <FactoryParsedItemsModal
        isOpen={isFactoryParsedItemsOpen}
        onClose={setIsFactoryParsedItemsOpen}
        factoryParsedItems={factoryParsedItems}
        factoryParsedFilename={factoryParsedFilename}
        factoryProjectName={factoryProjectName}
        factoryInvoiceNo={factoryInvoiceNo}
        factoryAllImages={factoryAllImages}
        materials={materials}
        availableFactories={availableFactories}
        allProjects={allProjects}
        allTypeNames={allTypeNames}
        imageSuggestionsOpen={imageSuggestionsOpen}
        imageSuggestions={imageSuggestions}
        factoryDimensionUnit={factoryDimensionUnit}
        setFactoryDimensionUnit={setFactoryDimensionUnit}
        factoryBulkMaterial={factoryBulkMaterial}
        setFactoryBulkMaterial={setFactoryBulkMaterial}
        factoryBulkColor={factoryBulkColor}
        setFactoryBulkColor={setFactoryBulkColor}
        factoryBulkFactory={factoryBulkFactory}
        setFactoryBulkFactory={setFactoryBulkFactory}
        factoryAdjustPercent={factoryAdjustPercent}
        setFactoryAdjustPercent={setFactoryAdjustPercent}
        factoryAdjustApplied={factoryAdjustApplied}
        setFactoryAdjustApplied={setFactoryAdjustApplied}
        handleProjectNameChange={handleProjectNameChange}
        autoMatchProjectItems={autoMatchProjectItems}
        applyFactoryBulkMaterial={applyFactoryBulkMaterial}
        applyFactoryBulkColor={applyFactoryBulkColor}
        applyFactoryBulkFactory={applyFactoryBulkFactory}
        applyFactoryPercentAdjust={applyFactoryPercentAdjust}
        updateFactoryParsedItem={updateFactoryParsedItem}
        loadImageSuggestions={loadImageSuggestions}
        setImageSuggestionsOpen={setImageSuggestionsOpen}
        saveFactoryParsedItems={saveFactoryParsedItems}
        toast={toast}
      />
      {/* Factory Manual Add Modal (Refactored Component) */}
      <FactoryManualAddModal
        isOpen={isFactoryManualAddOpen}
        onClose={setIsFactoryManualAddOpen}
        manualItem={factoryManualItem}
        setManualItem={setFactoryManualItem}
        onSave={handleSaveFactoryManualType}
        availableFactories={availableFactories}
        allTypeNames={allTypeNames}
        allProjects={allProjects}
      />

      {/* Factory Edit Modal (Refactored Component) */}
      <FactoryEditModal
        isOpen={isFactoryEditOpen}
        onClose={setIsFactoryEditOpen}
        editType={factoryEditType}
        setEditType={setFactoryEditType}
        onSave={handleUpdateFactoryType}
        availableFactories={availableFactories}
      />

      {/* Factory Delete Modal (Refactored Component) */}
      <FactoryDeleteModal
        isOpen={isFactoryDeleteOpen}
        onClose={setIsFactoryDeleteOpen}
        deleteType={factoryDeleteType}
        onConfirm={handleDeleteFactoryType}
      />

      {/* Merge Confirmation Dialog (Extracted Component) */}
      <MergeConfirmationDialog
        mergeConfirmation={mergeConfirmation}
        onCancel={handleCancelMerge}
        onConfirm={handleConfirmMerge}
      />

      {/* Factory Duplicate Modal (Refactored Component) */}
      <FactoryDuplicateModal
        isOpen={isFactoryDuplicateOpen}
        onClose={setIsFactoryDuplicateOpen}
        duplicateType={factoryDuplicateType}
        duplicateName={factoryDuplicateName}
        setDuplicateName={setFactoryDuplicateName}
        onConfirm={handleDuplicateFactoryType}
      />

      {/* Manual Add Modal */}
      {/* Manual Add Modal (Refactored Component) */}
      <ManualAddModal
        isOpen={isManualAddOpen}
        onClose={setIsManualAddOpen}
        manualAddData={manualAddData}
        setManualAddData={setManualAddData}
        onSubmit={handleManualAddSubmit}
        onImageUpload={handleManualImageUpload}
        isSubmitting={isManualAddSubmitting}
        materials={materials}
        aluminumSeries={aluminumSeries}
        allTypeNames={allTypeNames}
      />

      {/* Price Trend Modal */}
      <PriceTrendModal
        typeId={trendModalType?.id}
        typeName={trendModalType?.name}
        isOpen={!!trendModalType}
        onClose={() => setTrendModalType(null)}
      />

      {/* Duplicate Type Modal (Extracted Component) */}
      <DuplicateTypeModal
        duplicateType={duplicateModalType}
        onClose={setDuplicateModalType}
        duplicateNewName={duplicateNewName}
        setDuplicateNewName={setDuplicateNewName}
        onDuplicate={handleDuplicate}
        isDuplicating={isDuplicating}
      />

      {/* Price Adjustment Modal - Refactored Component */}
      <PriceAdjustModal
        isOpen={isPriceAdjustOpen}
        onClose={() => setIsPriceAdjustOpen(false)}
        adjustData={priceAdjustData}
        setAdjustData={setPriceAdjustData}
        onApply={handlePriceAdjust}
        isAdjusting={isAdjusting}
        quoteTypesCount={quoteTypes.length}
        selectedIdsCount={selectedTypeIds.length}
        materials={materials}
      />

      {/* Adjustment History Modal - Refactored Component */}
      <AdjustmentHistoryModal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        history={adjustmentHistory}
        isLoading={isLoadingHistory}
        onUndo={handleUndoAdjustment}
        isUndoing={isUndoing}
      />

      {/* Backup Manager Modal */}
      <BackupManager
        isOpen={isBackupManagerOpen}
        onClose={() => setIsBackupManagerOpen(false)}
      />

      {/* Price Alerts Manager Modal */}
      <PriceAlertsManager
        isOpen={isPriceAlertsOpen}
        onClose={() => setIsPriceAlertsOpen(false)}
      />

      {/* Quote History Manager Modal */}
      <QuoteHistoryManager
        isOpen={isQuoteHistoryOpen}
        onClose={() => setIsQuoteHistoryOpen(false)}
      />

      {/* Company Settings Manager Modal */}
      <CompanySettingsManager
        isOpen={isCompanySettingsOpen}
        onClose={() => setIsCompanySettingsOpen(false)}
      />

      {/* Projects Manager Modal */}
      <ProjectsManager
        isOpen={isProjectsManagerOpen}
        onClose={() => setIsProjectsManagerOpen(false)}
        onLoadProject={(data) => {
          // Use project items if available, otherwise use all quoteTypes
          const sourceItems = (data.items && data.items.length > 0) ? data.items : quoteTypes;
          
          // Transform project items to WindoorCraft format
          const projectItems = sourceItems.map(item => ({
            id: item.id,
            item_code: item.name?.split(' ')[0] || item.id?.slice(-6) || '',
            description: item.name || '',
            series: item.series || '',
            material: item.material || '',
            color: item.color || '',
            width_m: Math.sqrt((item.total_area || 0) / (item.entries_count || 1)) || 0,
            height_m: Math.sqrt((item.total_area || 0) / (item.entries_count || 1)) || 0,
            area_m2: (item.total_area || 0) / (item.entries_count || 1),  // Area per unit, not total
            quantity: item.entries_count || 1,
            db_cost_per_m2: item.average_cost_per_m2 || 0,
            calculated_value: (item.entries_count || 1) * (item.average_cost_per_m2 || 0) * ((item.total_area || 0) / (item.entries_count || 1)),
            matched_type_id: item.id,
            matched_type_name: item.name,
            match_confidence: 'loaded',
            image_data: item.image_data || null,
            observations: ''
          }));
          
          // Set parsed data with project info
          setWindoorCraftParsedData({
            items: projectItems,
            project_name: data.project?.name || '',
            // Pass installation and PDR data
            installation: {
              cosM2i: data.project?.installation_cos_m2i || '123',
              emoXM2: data.project?.installation_emo_x_m2 || '0.29',
              selloXM2: data.project?.installation_sello_x_m2 || '2.62',
              destaXM2: data.project?.installation_desta_x_m2 || '1.99',
              eppXM2: data.project?.installation_epp_x_m2 || '0.57',
              utiPercent: data.project?.installation_uti_percent || '11',
              adicional: data.project?.installation_adicional || '0'
            },
            pdr: {
              quantity: data.project?.pdr_quantity || '',
              unitValue: data.project?.pdr_unit_value || ''
            }
          });
          
          // Close projects modal and open WindoorCraft modal
          setIsProjectsManagerOpen(false);
          setIsWindoorCraftModalOpen(true);
          toast.success(`Proyecto "${data.project?.name}" cargado con ${projectItems.length} elementos`);
        }}
        onDuplicateProject={(newName) => {
          fetchQuoteTypes();
        }}
      />

      {/* User Management Dialog (Extracted Component) */}
      <UserManagementDialog
        isOpen={isUsersOpen}
        onClose={setIsUsersOpen}
      />
    </div>
  );
}

// Main App wrapped with AuthProvider
const AppWithAuth = () => {
  const { user, loading, logout, isAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage onSuccess={() => window.location.reload()} />;
  }

  return <App currentUser={user} onLogout={logout} isAdmin={isAdmin} />;
};

export default function AppRoot() {
  return (
    <AuthProvider>
      <AppWithAuth />
    </AuthProvider>
  );
}
